import { useState, useMemo, Fragment } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatPercent, formatNumber, getReturnColor } from '@/lib/utils'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts'

// ─── Types ────────────────────────────────────────────────────────────────────

interface FundResult {
  fund_id: number
  fund_name: string
  data_source: string
}

interface PeerGroupMember {
  id?: number
  fund_id: number
  fund_name: string
  data_source: string
  is_focus: boolean
}

interface PeerGroup {
  id: number
  name: string
  description: string
  member_count: number
  created_at?: string
  updated_at?: string
  members?: PeerGroupMember[]
}

interface DrawdownPoint {
  date: string
  drawdown: number
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface AnalysisResult {
  univariate: Record<string, any>[]
  correlation: { funds: string[]; matrix: number[][] }
  drawdowns: Record<string, DrawdownPoint[]>
  concurrent_down_months: {
    date: string
    funds_down: number
    total_funds: number
    returns: Record<string, number>
  }[]
  box_plots: Record<
    string,
    {
      min: number
      q1: number
      median: number
      q3: number
      max: number
      values: number[]
      names: string[]
    }
  >
  fund_count: number
  date_range: { start: string; end: string }
}

type CorrType = 'full' | 'drawdown' | 'gdic_mdd' | 'gdic_cvar95' | 'gdic_ulcer'

const CORR_LABELS: Record<CorrType, string> = {
  full: 'Standard',
  drawdown: 'Drawdown (CoD)',
  gdic_mdd: 'DIC — Max DD',
  gdic_cvar95: 'DIC — CVaR 95%',
  gdic_ulcer: 'DIC — Ulcer',
}

const CHART_COLORS = [
  '#FF6600', '#4A90D9', '#8BC34A', '#E91E63', '#9C27B0',
  '#00BCD4', '#FF9800', '#607D8B', '#3F51B5', '#795548',
  '#F44336', '#009688', '#CDDC39', '#2196F3', '#FF5722',
]

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PeerGroupAnalysis() {
  const queryClient = useQueryClient()

  // State
  const [searchSource, setSearchSource] = useState<'all' | 'alternatives' | 'hfr' | 'albourne'>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [focusFund, setFocusFund] = useState<FundResult | null>(null)
  const [peerFunds, setPeerFunds] = useState<FundResult[]>([])
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null)
  const [activeTab, setActiveTab] = useState<
    'univariate' | 'correlation' | 'drawdown' | 'box'
  >('univariate')
  const [months, setMonths] = useState(36)
  const [corrType, setCorrType] = useState<CorrType>('full')
  const [corrData, setCorrData] = useState<{
    funds: string[]
    matrix: number[][]
    type: string
  } | null>(null)
  const [expandedDownMonth, setExpandedDownMonth] = useState<string | null>(null)

  // Group management
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [groupName, setGroupName] = useState('')
  const [groupDescription, setGroupDescription] = useState('')
  const [editingGroup, setEditingGroup] = useState<PeerGroup | null>(null)

  // Queries
  const { data: searchResults = [], isFetching: isSearching } = useQuery({
    queryKey: ['peer-search', searchTerm, searchSource],
    queryFn: () =>
      api
        .get('/peer-group/search', { params: { q: searchTerm, source: searchSource } })
        .then((r) => r.data),
    enabled: searchTerm.length >= 2,
  })

  const { data: savedGroups = [], isLoading: groupsLoading } = useQuery<
    PeerGroup[]
  >({
    queryKey: ['peer-groups'],
    queryFn: () => api.get('/peer-group/groups').then((r) => r.data),
  })

  // Mutations
  const analyzeMutation = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: (payload: any) =>
      api.post('/peer-group/analyze', payload).then((r) => r.data),
    onSuccess: (data) => {
      setAnalysisResult(data)
      setCorrData(data.correlation ? { ...data.correlation, type: 'full' } : null)
      setCorrType('full')
    },
  })

  const corrMutation = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: (payload: any) =>
      api.post('/peer-group/correlations', payload).then((r) => r.data),
    onSuccess: (data) => setCorrData(data),
  })

  const createGroupMutation = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: (payload: any) =>
      api.post('/peer-group/groups', payload).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peer-groups'] })
      setShowSaveDialog(false)
      setGroupName('')
      setGroupDescription('')
    },
  })

  const updateGroupMutation = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: ({ id, payload }: { id: number; payload: any }) =>
      api.put(`/peer-group/groups/${id}`, payload).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peer-groups'] })
      setEditingGroup(null)
    },
  })

  const deleteGroupMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/peer-group/groups/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peer-groups'] })
      if (selectedGroupId === deleteGroupMutation.variables) {
        setSelectedGroupId(null)
        setFocusFund(null)
        setPeerFunds([])
      }
    },
  })

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const addPeer = (fund: FundResult) => {
    if (
      !peerFunds.find(
        (f) =>
          f.fund_id === fund.fund_id && f.data_source === fund.data_source,
      )
    ) {
      setPeerFunds([...peerFunds, fund])
    }
  }

  const removePeer = (idx: number) => {
    setPeerFunds(peerFunds.filter((_, i) => i !== idx))
  }

  const loadGroup = async (group: PeerGroup) => {
    setSelectedGroupId(group.id)
    try {
      const res = await api.get(`/peer-group/groups/${group.id}`)
      const full: PeerGroup = res.data
      const members = full.members || []
      const focus = members.find((m) => m.is_focus)
      const peers = members.filter((m) => !m.is_focus)
      setFocusFund(
        focus
          ? {
              fund_id: focus.fund_id,
              fund_name: focus.fund_name,
              data_source: focus.data_source,
            }
          : null,
      )
      setPeerFunds(
        peers.map((m) => ({
          fund_id: m.fund_id,
          fund_name: m.fund_name,
          data_source: m.data_source,
        })),
      )
      setAnalysisResult(null)
    } catch {
      // ignore
    }
  }

  const saveCurrentAsGroup = () => {
    if (!groupName.trim()) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const members: any[] = []
    if (focusFund) {
      members.push({
        fund_id: focusFund.fund_id,
        fund_name: focusFund.fund_name,
        data_source: focusFund.data_source,
        is_focus: true,
      })
    }
    peerFunds.forEach((f) => {
      members.push({
        fund_id: f.fund_id,
        fund_name: f.fund_name,
        data_source: f.data_source,
        is_focus: false,
      })
    })
    createGroupMutation.mutate({
      name: groupName,
      description: groupDescription,
      members,
    })
  }

  const updateCurrentGroup = () => {
    if (!selectedGroupId) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const members: any[] = []
    if (focusFund) {
      members.push({
        fund_id: focusFund.fund_id,
        fund_name: focusFund.fund_name,
        data_source: focusFund.data_source,
        is_focus: true,
      })
    }
    peerFunds.forEach((f) => {
      members.push({
        fund_id: f.fund_id,
        fund_name: f.fund_name,
        data_source: f.data_source,
        is_focus: false,
      })
    })
    updateGroupMutation.mutate({
      id: selectedGroupId,
      payload: { members },
    })
  }

  const getAllFunds = (): FundResult[] => {
    const all: FundResult[] = []
    if (focusFund) all.push(focusFund)
    all.push(...peerFunds)
    return all
  }

  const runAnalysis = () => {
    const allFunds = getAllFunds()
    if (allFunds.length < 2) return
    analyzeMutation.mutate({
      fund_ids: allFunds.map((f) => f.fund_id),
      fund_names: allFunds.map((f) => f.fund_name),
      data_sources: allFunds.map((f) => f.data_source),
      months,
    })
  }

  const switchCorrType = (type: CorrType) => {
    setCorrType(type)
    if (type === 'full' && analysisResult?.correlation) {
      setCorrData({ ...analysisResult.correlation, type: 'full' })
      return
    }
    const allFunds = getAllFunds()
    if (allFunds.length < 2) return
    corrMutation.mutate({
      fund_ids: allFunds.map((f) => f.fund_id),
      fund_names: allFunds.map((f) => f.fund_name),
      data_sources: allFunds.map((f) => f.data_source),
      months,
      type,
    })
  }

  const totalFunds = (focusFund ? 1 : 0) + peerFunds.length
  const activeGroupName = savedGroups.find(
    (g) => g.id === selectedGroupId,
  )?.name
  const isFocus = (name: string) =>
    focusFund != null && name === focusFund.fund_name

  // ─── Drawdown chart data ─────────────────────────────────────────────────

  const drawdownChartData = useMemo(() => {
    if (!analysisResult?.drawdowns) return []
    const funds = Object.keys(analysisResult.drawdowns)
    if (funds.length === 0) return []

    // Build merged date list
    const dateSet = new Set<string>()
    for (const pts of Object.values(analysisResult.drawdowns)) {
      for (const p of pts) dateSet.add(p.date)
    }
    const dates = Array.from(dateSet).sort()

    // Build lookup
    const lookup: Record<string, Record<string, number>> = {}
    for (const [fund, pts] of Object.entries(analysisResult.drawdowns)) {
      for (const p of pts) {
        if (!lookup[p.date]) lookup[p.date] = {}
        lookup[p.date][fund] = p.drawdown
      }
    }

    return dates.map((date) => ({
      date: date.slice(0, 7), // YYYY-MM
      ...lookup[date],
    }))
  }, [analysisResult?.drawdowns])

  const drawdownFunds = useMemo(() => {
    if (!analysisResult?.drawdowns) return []
    return Object.keys(analysisResult.drawdowns)
  }, [analysisResult?.drawdowns])

  // ─── Drawdown summary ─────────────────────────────────────────────────

  const drawdownSummary = useMemo(() => {
    if (!analysisResult?.univariate) return []
    return analysisResult.univariate
      .map((u) => ({
        name: u.name,
        max_drawdown: u.max_drawdown,
        current_drawdown: u.current_drawdown,
        recovery_days: u.recovery_days,
      }))
      .sort((a, b) => (a.max_drawdown ?? 0) - (b.max_drawdown ?? 0))
  }, [analysisResult?.univariate])

  // ─── Stress analysis ─────────────────────────────────────────────────

  const stressAnalysis = useMemo(() => {
    if (!analysisResult?.concurrent_down_months || !analysisResult?.univariate)
      return null
    const downMonths = analysisResult.concurrent_down_months
    if (downMonths.length === 0) return null

    const funds = analysisResult.univariate.map((u) => u.name)
    const results: Record<
      string,
      { positive_count: number; total: number; avg_return_when_positive: number }
    > = {}

    for (const fund of funds) {
      let posCount = 0
      let posSum = 0
      let total = 0
      for (const dm of downMonths) {
        const ret = dm.returns?.[fund]
        if (ret != null) {
          total++
          if (ret > 0) {
            posCount++
            posSum += ret
          }
        }
      }
      results[fund] = {
        positive_count: posCount,
        total,
        avg_return_when_positive: posCount > 0 ? posSum / posCount : 0,
      }
    }
    return results
  }, [analysisResult])

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Peer Group Analysis</h1>
        <p className="text-muted-foreground">
          Compare a focus fund against a peer group across 50+ metrics
        </p>
      </div>

      {/* Saved Groups Bar */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm">Saved Peer Groups</h3>
          <div className="flex gap-2">
            {selectedGroupId && (
              <button
                onClick={updateCurrentGroup}
                disabled={updateGroupMutation.isPending}
                className="text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-md disabled:opacity-50"
              >
                {updateGroupMutation.isPending ? 'Saving...' : 'Update Group'}
              </button>
            )}
            <button
              onClick={() => setShowSaveDialog(true)}
              disabled={totalFunds < 2}
              className="text-xs px-3 py-1.5 bg-muted text-foreground rounded-md disabled:opacity-50"
            >
              Save as New
            </button>
          </div>
        </div>

        {groupsLoading ? (
          <p className="text-xs text-muted-foreground">Loading...</p>
        ) : savedGroups.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No saved peer groups yet. Build a group below and save it.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {savedGroups.map((g) => (
              <div
                key={g.id}
                className={`group relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors ${
                  selectedGroupId === g.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80'
                }`}
              >
                <button
                  onClick={() => loadGroup(g)}
                  className="flex items-center gap-2"
                >
                  <span className="font-medium">{g.name}</span>
                  <span
                    className={`text-xs ${selectedGroupId === g.id ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}
                  >
                    {g.member_count} funds
                  </span>
                </button>
                <div className="hidden group-hover:flex items-center gap-1 ml-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setEditingGroup(g)
                    }}
                    className="text-xs opacity-70 hover:opacity-100"
                    title="Rename"
                  >
                    &#9998;
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (confirm(`Delete "${g.name}"?`))
                        deleteGroupMutation.mutate(g.id)
                    }}
                    className="text-xs text-red-500 opacity-70 hover:opacity-100"
                    title="Delete"
                  >
                    &#10005;
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Save Dialog */}
        {showSaveDialog && (
          <div className="mt-3 p-3 border border-border rounded-lg bg-background space-y-2">
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Group name (e.g. Macro Peers)"
              autoFocus
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
            />
            <input
              type="text"
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
              placeholder="Description (optional)"
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
            />
            <div className="flex gap-2">
              <button
                onClick={saveCurrentAsGroup}
                disabled={
                  !groupName.trim() || createGroupMutation.isPending
                }
                className="px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-xs disabled:opacity-50"
              >
                {createGroupMutation.isPending ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setShowSaveDialog(false)}
                className="px-3 py-1.5 bg-muted rounded-md text-xs"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Rename Dialog */}
        {editingGroup && (
          <div className="mt-3 p-3 border border-border rounded-lg bg-background space-y-2">
            <div className="text-xs text-muted-foreground">
              Rename &quot;{editingGroup.name}&quot;
            </div>
            <input
              type="text"
              defaultValue={editingGroup.name}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  updateGroupMutation.mutate({
                    id: editingGroup.id,
                    payload: {
                      name: (e.target as HTMLInputElement).value,
                    },
                  })
                }
              }}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setEditingGroup(null)}
                className="px-3 py-1.5 bg-muted rounded-md text-xs"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Fund Picker */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Search */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-semibold mb-3">Search Funds</h3>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by fund name..."
              className="flex-1 px-3 py-2 bg-background border border-border rounded-md text-sm"
            />
            <select
              value={searchSource}
              onChange={(e) => setSearchSource(e.target.value as typeof searchSource)}
              className="px-2 py-2 bg-background border border-border rounded-md text-sm"
            >
              <option value="all">All</option>
              <option value="hfr">HFR</option>
              <option value="alternatives">Internal</option>
              <option value="albourne">Albourne</option>
            </select>
          </div>
          {isSearching && (
            <p className="text-xs text-muted-foreground mt-2">Searching...</p>
          )}
          <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
            {searchResults.map((f: FundResult) => (
              <div
                key={`${f.data_source}-${f.fund_id}`}
                className="flex items-center justify-between px-2 py-1.5 hover:bg-muted rounded text-sm"
              >
                <span className="truncate flex-1">{f.fund_name}</span>
                <span className="text-xs text-muted-foreground mx-2">
                  {f.data_source}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setFocusFund(f)}
                    className="text-xs px-2 py-0.5 bg-primary text-primary-foreground rounded"
                  >
                    Focus
                  </button>
                  <button
                    onClick={() => addPeer(f)}
                    className="text-xs px-2 py-0.5 bg-muted rounded"
                  >
                    + Peer
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Selected Funds */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">
              Selected Funds
              {activeGroupName && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  ({activeGroupName})
                </span>
              )}
            </h3>
            {totalFunds > 0 && (
              <button
                onClick={() => {
                  setFocusFund(null)
                  setPeerFunds([])
                  setAnalysisResult(null)
                  setSelectedGroupId(null)
                }}
                className="text-xs text-destructive hover:underline"
              >
                Clear All
              </button>
            )}
          </div>
          {focusFund && (
            <div className="mb-3">
              <div className="text-xs text-muted-foreground uppercase">
                Focus Fund
              </div>
              <div className="flex items-center justify-between bg-orange-500/10 border-l-4 border-orange-500 px-3 py-2 rounded-md mt-1">
                <div>
                  <span className="text-sm font-bold">
                    {focusFund.fund_name}
                  </span>
                  <span className="text-xs text-muted-foreground ml-2">
                    {focusFund.data_source}
                  </span>
                </div>
                <button
                  onClick={() => setFocusFund(null)}
                  className="text-xs text-destructive"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
          <div className="text-xs text-muted-foreground uppercase mb-1">
            Peer Group ({peerFunds.length})
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {peerFunds.map((f, i) => (
              <div
                key={i}
                className="flex items-center justify-between px-3 py-1.5 bg-muted rounded-md text-sm"
              >
                <div className="truncate flex-1">
                  <span>{f.fund_name}</span>
                  <span className="text-xs text-muted-foreground ml-2">
                    {f.data_source}
                  </span>
                </div>
                <button
                  onClick={() => removePeer(i)}
                  className="text-xs text-destructive ml-2"
                >
                  x
                </button>
              </div>
            ))}
            {peerFunds.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No peers selected
              </p>
            )}
          </div>

          {/* Albourne Import placeholder */}
          <div className="mt-3 p-3 border border-dashed border-border rounded-lg bg-muted/30">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-medium text-muted-foreground">Import from Albourne</span>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Auto-populate peer groups from Albourne peer universe
                </p>
              </div>
              <button
                disabled
                className="text-xs px-3 py-1.5 bg-muted text-muted-foreground rounded-md cursor-not-allowed opacity-50"
                title="Coming soon"
              >
                Import Peers
              </button>
            </div>
          </div>

          {/* Analysis controls */}
          <div className="mt-3 flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Period:</label>
              <select
                value={months}
                onChange={(e) => setMonths(Number(e.target.value))}
                className="px-2 py-1.5 bg-background border border-border rounded text-sm"
              >
                <option value={36}>3 years</option>
                <option value={60}>5 years</option>
                <option value={120}>10 years</option>
                <option value={240}>20 years</option>
              </select>
            </div>
            <button
              onClick={runAnalysis}
              disabled={totalFunds < 2 || analyzeMutation.isPending}
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
            >
              {analyzeMutation.isPending ? 'Analyzing...' : 'Run Analysis'}
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {analysisResult && (
        <>
          {/* Date range info */}
          <div className="text-xs text-muted-foreground">
            Analysis period: {analysisResult.date_range.start.slice(0, 10)} to{' '}
            {analysisResult.date_range.end.slice(0, 10)} |{' '}
            {analysisResult.fund_count} funds
          </div>

          <div className="bg-card border border-border rounded-lg">
            {/* Tabs */}
            <div className="flex border-b border-border">
              {(
                [
                  { key: 'univariate', label: 'Metrics' },
                  { key: 'correlation', label: 'Correlations' },
                  { key: 'drawdown', label: 'Drawdowns' },
                  { key: 'box', label: 'Box Plots' },
                ] as const
              ).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === key
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="p-4">
              {/* ── Univariate Tab ──────────────────────────────────────── */}
              {activeTab === 'univariate' && (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="sticky left-0 bg-card z-10">Fund</th>
                        <th className="text-right">Ann Return</th>
                        <th className="text-right">Ann Vol</th>
                        <th className="text-right">Sharpe</th>
                        <th className="text-right">Sortino</th>
                        <th className="text-right">Max DD</th>
                        <th className="text-right">Current DD</th>
                        <th className="text-right">Calmar</th>
                        <th className="text-right">Omega</th>
                        <th className="text-right">CVaR 95%</th>
                        <th className="text-right">Win Rate</th>
                        <th className="text-right">Best Mo</th>
                        <th className="text-right">Worst Mo</th>
                        <th className="text-right">Ulcer Idx</th>
                        <th className="text-right">Ulcer Ratio</th>
                        <th className="text-right">Adj Sharpe</th>
                        <th className="text-right">Skew</th>
                        <th className="text-right">Kurt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      {analysisResult.univariate.map((row: any) => {
                        const focus = isFocus(row.name)
                        return (
                          <tr
                            key={row.name}
                            className={
                              focus
                                ? 'bg-orange-500/5 font-semibold'
                                : ''
                            }
                            style={
                              focus
                                ? {
                                    borderLeft: '4px solid #FF6600',
                                  }
                                : undefined
                            }
                          >
                            <td className="font-medium sticky left-0 bg-card">
                              {row.name}
                            </td>
                            <td
                              className={`text-right font-mono ${getReturnColor(row.ann_return)}`}
                            >
                              {formatPercent(row.ann_return)}
                            </td>
                            <td className="text-right font-mono">
                              {formatPercent(row.ann_volatility)}
                            </td>
                            <td className="text-right font-mono">
                              {formatNumber(row.sharpe_ratio)}
                            </td>
                            <td className="text-right font-mono">
                              {formatNumber(row.sortino_ratio)}
                            </td>
                            <td className="text-right font-mono text-red-600">
                              {formatPercent(row.max_drawdown)}
                            </td>
                            <td className="text-right font-mono text-red-600">
                              {formatPercent(row.current_drawdown)}
                            </td>
                            <td className="text-right font-mono">
                              {formatNumber(row.calmar_ratio)}
                            </td>
                            <td className="text-right font-mono">
                              {formatNumber(row.omega_ratio)}
                            </td>
                            <td className="text-right font-mono text-red-600">
                              {formatPercent(row.cvar_95)}
                            </td>
                            <td className="text-right font-mono">
                              {formatPercent(row.win_rate)}
                            </td>
                            <td className="text-right font-mono text-green-600">
                              {formatPercent(row.best_month)}
                            </td>
                            <td className="text-right font-mono text-red-600">
                              {formatPercent(row.worst_month)}
                            </td>
                            <td className="text-right font-mono">
                              {formatNumber(row.ulcer_index)}
                            </td>
                            <td className="text-right font-mono">
                              {formatNumber(row.ulcer_ratio)}
                            </td>
                            <td className="text-right font-mono">
                              {formatNumber(row.adjusted_sharpe)}
                            </td>
                            <td className="text-right font-mono">
                              {formatNumber(row.skewness)}
                            </td>
                            <td className="text-right font-mono">
                              {formatNumber(row.kurtosis)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Correlation Tab ─────────────────────────────────────── */}
              {activeTab === 'correlation' && (
                <div>
                  {/* Correlation type selector */}
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span className="text-xs text-muted-foreground">
                      Type:
                    </span>
                    {(Object.keys(CORR_LABELS) as CorrType[]).map((t) => (
                      <button
                        key={t}
                        onClick={() => switchCorrType(t)}
                        disabled={corrMutation.isPending}
                        className={`px-3 py-1 rounded text-xs border transition-colors ${
                          corrType === t
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-muted text-muted-foreground border-border hover:border-primary'
                        }`}
                      >
                        {CORR_LABELS[t]}
                      </button>
                    ))}
                    {corrMutation.isPending && (
                      <span className="text-xs text-muted-foreground ml-2">
                        Computing...
                      </span>
                    )}
                  </div>

                  {corrData && (
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th className="sticky left-0 bg-card z-10"></th>
                            {corrData.funds.map((f) => (
                              <th key={f} className="text-right text-xs">
                                {f.length > 15 ? f.slice(0, 12) + '...' : f}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {corrData.funds.map((fund, i) => (
                            <tr
                              key={fund}
                              className={
                                isFocus(fund)
                                  ? 'bg-orange-500/5 font-semibold'
                                  : ''
                              }
                              style={
                                isFocus(fund)
                                  ? { borderLeft: '4px solid #FF6600' }
                                  : undefined
                              }
                            >
                              <td className="font-medium text-xs sticky left-0 bg-card">
                                {fund.length > 20
                                  ? fund.slice(0, 17) + '...'
                                  : fund}
                              </td>
                              {corrData.matrix[i].map((val, j) => (
                                <td
                                  key={j}
                                  className="text-right font-mono text-xs"
                                  style={{
                                    backgroundColor:
                                      i === j
                                        ? 'transparent'
                                        : `rgba(${val > 0 ? '59,130,246' : '239,68,68'}, ${Math.abs(val) * 0.3})`,
                                  }}
                                >
                                  {i === j ? '1.00' : val.toFixed(2)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── Drawdown Tab ────────────────────────────────────────── */}
              {activeTab === 'drawdown' && (
                <div className="space-y-6">
                  {/* Drawdown chart */}
                  {drawdownChartData.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-3">
                        Drawdown Time Series
                      </h3>
                      <div style={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={drawdownChartData}>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="var(--border)"
                            />
                            <XAxis
                              dataKey="date"
                              tick={{ fontSize: 10 }}
                              interval="preserveStartEnd"
                            />
                            <YAxis
                              tickFormatter={(v: number) =>
                                `${(v * 100).toFixed(0)}%`
                              }
                              tick={{ fontSize: 10 }}
                              domain={['auto', 0]}
                            />
                            <Tooltip
                              formatter={(v: number) =>
                                `${(v * 100).toFixed(2)}%`
                              }
                              contentStyle={{
                                backgroundColor: 'var(--card)',
                                border: '1px solid var(--border)',
                                fontSize: 12,
                              }}
                            />
                            <Legend
                              wrapperStyle={{ fontSize: 11 }}
                            />
                            {drawdownFunds.map((fund, i) => (
                              <Line
                                key={fund}
                                type="monotone"
                                dataKey={fund}
                                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                                strokeWidth={isFocus(fund) ? 3 : 1.5}
                                dot={false}
                                name={
                                  fund.length > 25
                                    ? fund.slice(0, 22) + '...'
                                    : fund
                                }
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Drawdown summary table */}
                  {drawdownSummary.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-3">
                        Drawdown Summary
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Fund</th>
                              <th className="text-right">Max Drawdown</th>
                              <th className="text-right">Current DD</th>
                              <th className="text-right">Recovery (days)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {drawdownSummary.map((row) => (
                              <tr
                                key={row.name}
                                className={
                                  isFocus(row.name)
                                    ? 'bg-orange-500/5 font-semibold'
                                    : ''
                                }
                                style={
                                  isFocus(row.name)
                                    ? { borderLeft: '4px solid #FF6600' }
                                    : undefined
                                }
                              >
                                <td className="font-medium">{row.name}</td>
                                <td className="text-right font-mono text-red-600">
                                  {formatPercent(row.max_drawdown)}
                                </td>
                                <td className="text-right font-mono text-red-600">
                                  {formatPercent(row.current_drawdown)}
                                </td>
                                <td className="text-right font-mono">
                                  {row.recovery_days != null
                                    ? row.recovery_days
                                    : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Concurrent down months */}
                  {analysisResult.concurrent_down_months &&
                    analysisResult.concurrent_down_months.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3">
                          Concurrent Down Months
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Month</th>
                                <th className="text-right">Funds Down</th>
                                <th className="text-right">Total</th>
                                <th className="text-right">% Down</th>
                                <th className="text-right">Group Avg</th>
                                <th className="w-8"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                              {analysisResult.concurrent_down_months.map(
                                (row: any) => {
                                  const rets = Object.values(
                                    row.returns || {},
                                  ) as number[]
                                  const groupAvg =
                                    rets.length > 0
                                      ? rets.reduce((a, b) => a + b, 0) /
                                        rets.length
                                      : 0
                                  const isExpanded =
                                    expandedDownMonth === row.date
                                  return (
                                    <Fragment key={row.date}>
                                      <tr
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() =>
                                          setExpandedDownMonth(
                                            isExpanded ? null : row.date,
                                          )
                                        }
                                      >
                                        <td className="font-mono">
                                          {row.date.slice(0, 7)}
                                        </td>
                                        <td className="text-right font-mono text-red-600">
                                          {row.funds_down}
                                        </td>
                                        <td className="text-right font-mono">
                                          {row.total_funds}
                                        </td>
                                        <td className="text-right font-mono">
                                          {(
                                            (row.funds_down /
                                              row.total_funds) *
                                            100
                                          ).toFixed(0)}
                                          %
                                        </td>
                                        <td
                                          className={`text-right font-mono ${groupAvg < 0 ? 'text-red-600' : 'text-green-600'}`}
                                        >
                                          {(groupAvg * 100).toFixed(2)}%
                                        </td>
                                        <td className="text-center text-xs text-muted-foreground">
                                          {isExpanded ? '▾' : '▸'}
                                        </td>
                                      </tr>
                                      {isExpanded && row.returns && (
                                        <tr>
                                          <td
                                            colSpan={6}
                                            className="bg-muted/30 p-3"
                                          >
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                              {Object.entries(
                                                row.returns as Record<
                                                  string,
                                                  number
                                                >,
                                              )
                                                .sort(
                                                  ([, a], [, b]) =>
                                                    (a ?? 0) - (b ?? 0),
                                                )
                                                .map(([fund, ret]) => (
                                                  <div
                                                    key={fund}
                                                    className={`flex justify-between px-2 py-1 rounded ${
                                                      isFocus(fund)
                                                        ? 'bg-orange-500/10 font-bold border-l-2 border-orange-500'
                                                        : ''
                                                    }`}
                                                  >
                                                    <span className="truncate mr-2">
                                                      {fund.slice(0, 20)}
                                                    </span>
                                                    <span
                                                      className={`font-mono ${(ret ?? 0) < 0 ? 'text-red-600' : 'text-green-600'}`}
                                                    >
                                                      {(
                                                        (ret ?? 0) * 100
                                                      ).toFixed(2)}
                                                      %
                                                    </span>
                                                  </div>
                                                ))}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </Fragment>
                                  )
                                },
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                  {/* Stress resilience */}
                  {stressAnalysis && (
                    <div>
                      <h3 className="font-semibold mb-3">
                        Stress Resilience (positive returns during down months)
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Fund</th>
                              <th className="text-right">Win Rate</th>
                              <th className="text-right">
                                Positive / Total
                              </th>
                              <th className="text-right">
                                Avg Return (when +)
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(stressAnalysis)
                              .sort(
                                ([, a], [, b]) =>
                                  (b.total > 0
                                    ? b.positive_count / b.total
                                    : 0) -
                                  (a.total > 0
                                    ? a.positive_count / a.total
                                    : 0),
                              )
                              .map(([fund, stats]) => (
                                <tr
                                  key={fund}
                                  className={
                                    isFocus(fund)
                                      ? 'bg-orange-500/5 font-semibold'
                                      : ''
                                  }
                                  style={
                                    isFocus(fund)
                                      ? {
                                          borderLeft:
                                            '4px solid #FF6600',
                                        }
                                      : undefined
                                  }
                                >
                                  <td className="font-medium">{fund}</td>
                                  <td className="text-right font-mono">
                                    {stats.total > 0
                                      ? `${((stats.positive_count / stats.total) * 100).toFixed(0)}%`
                                      : '-'}
                                  </td>
                                  <td className="text-right font-mono text-muted-foreground">
                                    {stats.positive_count} / {stats.total}
                                  </td>
                                  <td className="text-right font-mono text-green-600">
                                    {stats.avg_return_when_positive > 0
                                      ? `+${(stats.avg_return_when_positive * 100).toFixed(2)}%`
                                      : '-'}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Box Plot Tab ────────────────────────────────────────── */}
              {activeTab === 'box' && analysisResult.box_plots && (
                <div className="space-y-4">
                  <h3 className="font-semibold">Distribution Comparison</h3>
                  {Object.entries(analysisResult.box_plots).map(
                    ([metric, data]) => {
                      const isPercent = [
                        'ann_return',
                        'ann_volatility',
                        'max_drawdown',
                      ].includes(metric)
                      const fmt = (v: number) =>
                        isPercent
                          ? `${(v * 100).toFixed(2)}%`
                          : v.toFixed(3)
                      const range = data.max - data.min || 1
                      const pct = (v: number) =>
                        ((v - data.min) / range) * 100

                      // Find focus fund value
                      const focusIdx = focusFund
                        ? data.names?.indexOf(focusFund.fund_name)
                        : -1
                      const focusVal =
                        focusIdx != null &&
                        focusIdx >= 0 &&
                        data.values
                          ? data.values[focusIdx]
                          : null

                      return (
                        <div
                          key={metric}
                          className="bg-muted rounded-lg p-4"
                        >
                          <div className="text-sm font-medium mb-2 capitalize">
                            {metric.replace(/_/g, ' ')}
                          </div>
                          <div className="flex items-center gap-4 text-xs font-mono">
                            <span>Min: {fmt(data.min)}</span>
                            <span>Q1: {fmt(data.q1)}</span>
                            <span className="font-semibold">
                              Median: {fmt(data.median)}
                            </span>
                            <span>Q3: {fmt(data.q3)}</span>
                            <span>Max: {fmt(data.max)}</span>
                            {focusVal != null && (
                              <span className="text-orange-600 font-bold ml-2">
                                Focus: {fmt(focusVal)}
                              </span>
                            )}
                          </div>

                          {/* Box plot visualization */}
                          <div className="mt-3 relative h-10">
                            {/* Whisker line (min to max) */}
                            <div
                              className="absolute top-1/2 h-px bg-foreground/30"
                              style={{
                                left: `${pct(data.min)}%`,
                                width: `${pct(data.max) - pct(data.min)}%`,
                              }}
                            />
                            {/* Min whisker cap */}
                            <div
                              className="absolute top-1/2 -translate-y-1/2 w-px h-4 bg-foreground/50"
                              style={{ left: `${pct(data.min)}%` }}
                            />
                            {/* Max whisker cap */}
                            <div
                              className="absolute top-1/2 -translate-y-1/2 w-px h-4 bg-foreground/50"
                              style={{ left: `${pct(data.max)}%` }}
                            />
                            {/* IQR box (Q1 to Q3) */}
                            <div
                              className="absolute top-1/2 -translate-y-1/2 h-7 bg-primary/20 border border-primary/40 rounded"
                              style={{
                                left: `${pct(data.q1)}%`,
                                width: `${pct(data.q3) - pct(data.q1)}%`,
                              }}
                            />
                            {/* Median line */}
                            <div
                              className="absolute top-1/2 -translate-y-1/2 w-0.5 h-7 bg-primary"
                              style={{ left: `${pct(data.median)}%` }}
                            />
                            {/* Individual fund dots */}
                            {data.values?.map((v, i) => (
                              <div
                                key={i}
                                className={`absolute top-1/2 -translate-y-1/2 rounded-full ${
                                  isFocus(data.names?.[i] || '')
                                    ? 'w-3.5 h-3.5 bg-orange-500 border-2 border-orange-700 z-10'
                                    : 'w-2 h-2 bg-foreground/40'
                                }`}
                                style={{
                                  left: `calc(${pct(v)}% - ${isFocus(data.names?.[i] || '') ? 7 : 4}px)`,
                                }}
                                title={`${data.names?.[i]}: ${fmt(v)}`}
                              />
                            ))}
                          </div>
                        </div>
                      )
                    },
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
